import { useQuery } from "@tanstack/react-query";
import { lazy, Suspense, useEffect, useState, type ReactNode } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Shell } from "./components/Shell";
import { ToastProvider } from "./components/Toast";
import { Empty, ErrorNote, Spinner } from "./components/ui";
import { api } from "./lib/api";
import { supabase, type Session } from "./lib/supabase";
import { Archived } from "./pages/Archived";
import { LeadDetail } from "./pages/LeadDetail";
import { Leads } from "./pages/Leads";
import { AuthCallback } from "./pages/AuthCallback";
import { Login } from "./pages/Login";
import { Buyers, Investors } from "./pages/Capital";
import { DealDetail } from "./pages/DealDetail";
import { Deals } from "./pages/Deals";
import { Engagements } from "./pages/Engagements";
import { Overview } from "./pages/Overview";
import { Vendors } from "./pages/Vendors";
import { Tasks } from "./pages/Tasks";

/**
 * Analytics is lazy-loaded so Recharts never lands in the main chunk (§9 Phase 4 gate: main
 * bundle <= 200 kB gzipped). Wired now, while the page is still a placeholder, so the shape is
 * in place before the charts arrive.
 */
const Analytics = lazy(() =>
  import("./pages/Analytics").then((m) => ({ default: m.Analytics })),
);
const ActivityLog = lazy(() =>
  import("./pages/Evidence").then((m) => ({ default: m.ActivityLog })),
);
const AgentRuns = lazy(() =>
  import("./pages/Evidence").then((m) => ({ default: m.AgentRuns })),
);
const Ingest = lazy(() => import("./pages/Evidence").then((m) => ({ default: m.Ingest })));
// v2.10 P4.2 / P3.3: the phone's start page and the text templates.
const Today = lazy(() => import("./pages/Today").then((m) => ({ default: m.Today })));
const SmsTemplates = lazy(() => import("./pages/SmsTemplates").then((m) => ({ default: m.SmsTemplates })));
// v2.2 P5.6: the audit, the error ledger and the scheduler's record.
const System = lazy(() => import("./pages/System").then((m) => ({ default: m.System })));

/**
 * The Curative module is lazy-loaded as one chunk, for the same reason Analytics is: it is a large
 * surface most page loads never touch, and the entry bundle has a 200 kB gzipped budget to hold.
 */
const CurativeDashboard = lazy(() =>
  import("./curative/Dashboard").then((m) => ({ default: m.CurativeDashboard })),
);
const CurativeInbox = lazy(() =>
  import("./curative/Inbox").then((m) => ({ default: m.CurativeInbox })),
);
const CurativeSettings = lazy(() =>
  import("./curative/Settings").then((m) => ({ default: m.CurativeSettings })),
);
const CurativeImport = lazy(() =>
  import("./curative/Import").then((m) => ({ default: m.CurativeImport })),
);
const CurativeBoards = lazy(() =>
  import("./curative/Boards").then((m) => ({ default: m.Boards })),
);
const CurativeToday = lazy(() =>
  import("./curative/TodayQueue").then((m) => ({ default: m.CurativeToday })),
);
const CurativeBoard = lazy(() => import("./curative/Board").then((m) => ({ default: m.Board })));
const CurativeDeal = lazy(() => import("./curative/Deal").then((m) => ({ default: m.Deal })));
const CurativeReview = lazy(() =>
  import("./curative/Scorecard").then((m) => ({ default: m.CurativeReview })),
);
const CurativeSmartLists = lazy(() =>
  import("./curative/SmartLists").then((m) => ({ default: m.CurativeSmartLists })),
);

function Lazy({ children }: { children: ReactNode }) {
  return <Suspense fallback={<Spinner />}>{children}</Suspense>;
}

function Placeholder({ title }: { title: string }) {
  return (
    <div className="p-4">
      <h1 className="mb-2 text-lg font-semibold tracking-tight">{title}</h1>
      <Empty
        title="Not built yet."
        hint="This screen arrives in a later phase. PROGRESS.md tracks which."
      />
    </div>
  );
}

function useSession() {
  const [session, setSession] = useState<Session | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let active = true;
    void supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      setSession(data.session);
      setReady(true);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next);
      setReady(true);
    });
    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  return { session, ready };
}

function AuthedApp() {
  const { data: me, isLoading, error } = useQuery({ queryKey: ["me"], queryFn: () => api.me() });

  if (isLoading) return <Spinner label="Signing in…" />;
  if (error) {
    return (
      <div className="mx-auto max-w-md p-6">
        <ErrorNote error={error} />
        <p className="mt-2 text-xs text-fg-muted">
          If this says the address is not on the allow-list, ask Sam to add it to ALLOWED_EMAILS.
        </p>
        <button
          type="button"
          className="mt-3 text-xs underline"
          onClick={() => void supabase.auth.signOut()}
        >
          Sign out
        </button>
      </div>
    );
  }
  if (!me) return null;

  return (
    <Shell email={me.email} role={me.role}>
      <Routes>
        <Route path="/" element={<Overview />} />
        <Route path="/today" element={<Lazy><Today /></Lazy>} />
        <Route path="/sms-templates" element={<Lazy><SmsTemplates /></Lazy>} />
        <Route path="/leads" element={<Leads />} />
        <Route path="/leads/:leadId" element={<LeadDetail />} />
        <Route path="/archived" element={<Archived />} />
        <Route path="/tasks" element={<Tasks />} />
        <Route path="/deals" element={<Deals />} />
        <Route path="/offers" element={<Placeholder title="Offers" />} />
        <Route path="/vendors" element={<Vendors />} />
        <Route path="/engagements" element={<Engagements />} />
        <Route path="/investors" element={<Investors />} />
        <Route path="/buyers" element={<Buyers />} />
        <Route path="/parcels" element={<Placeholder title="Parcels" />} />
        <Route path="/parcels/:parcelId" element={<Placeholder title="Parcel" />} />
        <Route path="/documents" element={<Placeholder title="Documents" />} />
        <Route path="/comps" element={<Placeholder title="Comps" />} />
        <Route path="/county-recipes" element={<Placeholder title="County Recipes" />} />
        <Route path="/activity" element={<Lazy><ActivityLog /></Lazy>} />
        <Route path="/analytics" element={<Lazy><Analytics /></Lazy>} />
        <Route path="/agent-runs" element={<Lazy><AgentRuns /></Lazy>} />
        <Route path="/ingest" element={<Lazy><Ingest /></Lazy>} />
        <Route path="/system" element={<Lazy><System /></Lazy>} />
        <Route path="/deals/:dealId" element={<DealDetail />} />
        {/* v2.0 Curative (C0.5). Every route is under /curative/, so no v1 route changes. */}
        <Route path="/curative/today" element={<Lazy><CurativeToday /></Lazy>} />
        <Route path="/curative/dashboard" element={<Lazy><CurativeDashboard /></Lazy>} />
        <Route path="/curative/inbox" element={<Lazy><CurativeInbox /></Lazy>} />
        <Route path="/curative/import" element={<Lazy><CurativeImport /></Lazy>} />
        <Route path="/curative/boards" element={<Lazy><CurativeBoards /></Lazy>} />
        <Route path="/curative/boards/:boardId" element={<Lazy><CurativeBoard /></Lazy>} />
        <Route path="/curative/deals/:dealId" element={<Lazy><CurativeDeal /></Lazy>} />
        <Route path="/curative/review" element={<Lazy><CurativeReview /></Lazy>} />
        <Route path="/curative/smart-lists" element={<Lazy><CurativeSmartLists /></Lazy>} />
        <Route path="/curative/settings" element={<Lazy><CurativeSettings /></Lazy>} />
        <Route path="/vendors/:vendorId" element={<Vendors />} />
        <Route path="*" element={<Placeholder title="Not found" />} />
      </Routes>
    </Shell>
  );
}

export function App() {
  const { session, ready } = useSession();

  return (
    <ToastProvider>
      <BrowserRouter>
        <Routes>
          {/* Ahead of the session gate on purpose: completing this route is what CREATES the
              session, so a callback that required one could never sign anyone in. */}
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route
            path="*"
            element={!ready ? <Spinner /> : session ? <AuthedApp /> : <Login />}
          />
        </Routes>
      </BrowserRouter>
    </ToastProvider>
  );
}
